import { CollectionSummary } from "@/types/collection"
import { InspirationSummary } from "@/types/inspiration"
import {
  GetNotificationSettingsResponse,
  NotificationSetting,
} from "@/types/notification-settings"
import { SettingTabType } from "@/types/settings"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "@/lib/storage/redux-store"

export const ProfileTabGuides = "guides"
export const ProfileTabCollections = "collections"
export const ProfileTabReviews = "reviews"
export const ProfileTabDefault = ProfileTabGuides

export type ProfileTabType =
  | typeof ProfileTabGuides
  | typeof ProfileTabCollections
  | typeof ProfileTabReviews

export const ProfileTabTypeValues: ProfileTabType[] = [
  ProfileTabGuides,
  ProfileTabCollections,
  ProfileTabReviews,
]

export interface ProfileSliceState {
  // Profile page
  userProfileGuidesCount: number
  userProfileCollectionsCount: number
  userProfileLoadingGuides: boolean
  userProfileLoadingCollections: boolean
  userProfilePaginatedGuides: InspirationSummary[] | undefined
  userProfilePaginatedCollections: CollectionSummary[] | undefined
  profileTab: ProfileTabType
  userProfileCurrInspirationPage: number
  userProfileMaxInspirationPage: number
  userProfileCurrCollectionsPage: number
  userProfileMaxCollectionsPage: number

  // Settings page section. null when the URL names none: phones show the list
  // of sections, wider screens show Account.
  settingTab: SettingTabType | null

  // Notification settings
  notificationSettings: GetNotificationSettingsResponse | null
  notificationSettingsLoading: boolean
  notificationSettingsError: string | null
  notificationSettingsSaving: boolean
  notificationSettingsResetting: boolean
  notificationSettingsHasPendingChanges: boolean
  notificationSettingsCurrentView: "list" | "detail"
  selectedNotificationSetting: {
    setting: NotificationSetting
    groupKey: string
  } | null

  isInitialized: boolean
}

export const profileSliceInitialState: ProfileSliceState = {
  userProfileGuidesCount: 0,
  userProfileCollectionsCount: 0,
  userProfileLoadingGuides: false,
  userProfilePaginatedGuides: undefined,
  profileTab: ProfileTabDefault,
  userProfileCurrInspirationPage: 1,
  userProfileMaxInspirationPage: 1,
  userProfileLoadingCollections: false,
  userProfilePaginatedCollections: undefined,
  userProfileCurrCollectionsPage: 1,
  userProfileMaxCollectionsPage: 1,

  // Profile settings page
  settingTab: null,

  // Notification settings
  notificationSettings: null,
  notificationSettingsLoading: false,
  notificationSettingsError: null,
  notificationSettingsSaving: false,
  notificationSettingsResetting: false,
  notificationSettingsHasPendingChanges: false,
  notificationSettingsCurrentView: "list",
  selectedNotificationSetting: null,

  isInitialized: false,
}

// console.log('ProfileSlice: Initial State:', profileSliceInitialState);

const profileSlice = createSlice({
  name: "profileSlice",
  initialState: profileSliceInitialState,
  reducers: {
    setUserProfileGuidesCount: (state, action: PayloadAction<number>) => {
      state.userProfileGuidesCount = action.payload
    },
    setUserProfileCollectionsCount: (state, action: PayloadAction<number>) => {
      state.userProfileCollectionsCount = action.payload
    },
    setUserProfileLoadingGuides: (state, action: PayloadAction<boolean>) => {
      state.userProfileLoadingGuides = action.payload
    },
    setUserProfilePaginatedGuides: (
      state,
      action: PayloadAction<InspirationSummary[]>
    ) => {
      state.userProfilePaginatedGuides = action.payload
    },
    setUserProfileTab: (state, action: PayloadAction<ProfileTabType>) => {
      state.profileTab = action.payload
    },
    setUserSettingTab: (
      state,
      action: PayloadAction<SettingTabType | null>
    ) => {
      state.settingTab = action.payload
    },
    setUserProfileCurrInspirationPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.userProfileCurrInspirationPage = action.payload
    },
    setUserProfileMaxInspirationPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.userProfileMaxInspirationPage = action.payload
    },
    resetUserProfile: () => profileSliceInitialState,
    addUserProfilePaginatedGuides: (
      state,
      action: PayloadAction<InspirationSummary[]>
    ) => {
      state.userProfilePaginatedGuides = [
        ...(state.userProfilePaginatedGuides || []),
        ...action.payload,
      ]
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload
    },
    setUserProfilePaginatedCollections: (
      state,
      action: PayloadAction<CollectionSummary[]>
    ) => {
      state.userProfilePaginatedCollections = action.payload
    },
    setUserProfileLoadingCollections: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.userProfileLoadingCollections = action.payload
    },
    setUserProfileCurrCollectionsPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.userProfileCurrCollectionsPage = action.payload
    },
    setUserProfileMaxCollectionsPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.userProfileMaxCollectionsPage = action.payload
    },
    addUserProfilePaginatedCollections: (
      state,
      action: PayloadAction<CollectionSummary[]>
    ) => {
      state.userProfilePaginatedCollections = [
        ...(state.userProfilePaginatedCollections || []),
        ...action.payload,
      ]
    },

    // Notification settings reducers
    setNotificationSettings: (
      state,
      action: PayloadAction<GetNotificationSettingsResponse>
    ) => {
      state.notificationSettings = action.payload
    },
    setNotificationSettingsLoading: (state, action: PayloadAction<boolean>) => {
      state.notificationSettingsLoading = action.payload
    },
    setNotificationSettingsError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.notificationSettingsError = action.payload
    },
    setNotificationSettingsSaving: (state, action: PayloadAction<boolean>) => {
      state.notificationSettingsSaving = action.payload
    },
    setNotificationSettingsResetting: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.notificationSettingsResetting = action.payload
    },
    setNotificationSettingsHasPendingChanges: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.notificationSettingsHasPendingChanges = action.payload
    },
    setNotificationSettingsCurrentView: (
      state,
      action: PayloadAction<"list" | "detail">
    ) => {
      state.notificationSettingsCurrentView = action.payload
    },
    setSelectedNotificationSetting: (
      state,
      action: PayloadAction<{
        setting: NotificationSetting
        groupKey: string
      } | null>
    ) => {
      state.selectedNotificationSetting = action.payload
    },
  },
})

// console.log('ProfileSlice: Initial State Finish:', profileSlice);

const selectSliceState = (state: RootState) => state.profileSlice

export const selectUserProfileGuidesCount = createSelector(
  [selectSliceState],
  (state) => state.userProfileGuidesCount
)

export const selectUserProfileCollectionsCount = createSelector(
  [selectSliceState],
  (state) => state.userProfileCollectionsCount
)

export const selectUserProfileLoadingGuides = createSelector(
  [selectSliceState],
  (state) => state.userProfileLoadingGuides
)

export const selectUserProfilePaginatedGuides = createSelector(
  [selectSliceState],
  (state) => state.userProfilePaginatedGuides
)

export const selectUserProfileTab = createSelector(
  [selectSliceState],
  (state) => state.profileTab
)

export const selectUserSettingTab = createSelector(
  [selectSliceState],
  (state) => state.settingTab
)

export const selectUserProfileCurrInspirationPage = createSelector(
  [selectSliceState],
  (state) => state.userProfileCurrInspirationPage
)

export const selectUserProfileMaxInspirationPage = createSelector(
  [selectSliceState],
  (state) => state.userProfileMaxInspirationPage
)

export const selectProfileInitialized = createSelector(
  [selectSliceState],
  (state) => state.isInitialized
)

export const selectUserProfilePaginatedCollections = createSelector(
  [selectSliceState],
  (state) => state.userProfilePaginatedCollections
)

export const selectUserProfileLoadingCollections = createSelector(
  [selectSliceState],
  (state) => state.userProfileLoadingCollections
)

export const selectUserProfileCurrCollectionsPage = createSelector(
  [selectSliceState],
  (state) => state.userProfileCurrCollectionsPage
)

export const selectUserProfileMaxCollectionsPage = createSelector(
  [selectSliceState],
  (state) => state.userProfileMaxCollectionsPage
)

// Notification settings selectors
export const selectNotificationSettings = createSelector(
  [selectSliceState],
  (state) => state.notificationSettings
)

export const selectNotificationSettingsLoading = createSelector(
  [selectSliceState],
  (state) => state.notificationSettingsLoading
)

export const selectNotificationSettingsError = createSelector(
  [selectSliceState],
  (state) => state.notificationSettingsError
)

export const selectNotificationSettingsCurrentView = createSelector(
  [selectSliceState],
  (state) => state.notificationSettingsCurrentView
)

export const selectSelectedNotificationSetting = createSelector(
  [selectSliceState],
  (state) => state.selectedNotificationSetting
)

export const selectNotificationSettingsSaving = createSelector(
  [selectSliceState],
  (state) => state.notificationSettingsSaving
)

export const selectNotificationSettingsResetting = createSelector(
  [selectSliceState],
  (state) => state.notificationSettingsResetting
)

export const selectNotificationSettingsHasPendingChanges = createSelector(
  [selectSliceState],
  (state) => state.notificationSettingsHasPendingChanges
)

export const selectNotificationSettingsAreAtDefaults = createSelector(
  [selectNotificationSettings],
  (notificationSettings) => {
    if (!notificationSettings) return true
    return notificationSettings.is_at_defaults
  }
)

export const {
  setUserProfileGuidesCount,
  setUserProfileCollectionsCount,
  setUserProfileLoadingGuides,
  setUserProfilePaginatedGuides,
  setUserProfileTab,
  resetUserProfile,
  setUserProfileCurrInspirationPage,
  setUserProfileMaxInspirationPage,
  addUserProfilePaginatedGuides,
  setInitialized,
  setUserProfilePaginatedCollections,
  setUserProfileLoadingCollections,
  setUserProfileCurrCollectionsPage,
  setUserProfileMaxCollectionsPage,
  addUserProfilePaginatedCollections,
  setNotificationSettings,
  setNotificationSettingsLoading,
  setNotificationSettingsError,
  setNotificationSettingsSaving,
  setNotificationSettingsResetting,
  setNotificationSettingsHasPendingChanges,
  setNotificationSettingsCurrentView,
  setSelectedNotificationSetting,
} = profileSlice.actions

export default profileSlice
